import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import ContainerMaxWidthXl from '@deltek/specification-client/core/Surfaces/ContainerMaxWidthXl'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { AppBar, IconButton, Input, InputAdornment } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import { Theme, makeStyles } from '@material-ui/core/styles'
import CheckIcon from '@material-ui/icons/Check'
import ClearIcon from '@material-ui/icons/Clear'
import SearchIcon from '@material-ui/icons/Search'
import clsx from 'clsx'
import { CategoryPanel } from 'models/category'
import {
    GetProjectElementNodesInput,
    NodeSearchFilter,
} from 'models/getProjectElementNodesInput'
import React, { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
    escapeSearchText,
    normalizeSearchText,
    unescapeSearchText,
} from 'shared/utils/stringUtils'
import { MaxElementsPerBatch } from '../..'
import AdvancedSearch from './AdvancedSearch'

const useShowHideBarStyles = makeStyles((theme: Theme) => ({
    searchBarContainer: {
        position: 'absolute',
        top: pxToRem(15),
        backgroundColor: 'transparent',
    },
    searchFieldContainer: {
        width: '50%',
        height: pxToRem(35),
        backgroundColor: theme.palette.common.white,
        border: '1px solid #C9C9C9',
        marginLeft: theme.spacing(2.8),
        borderRadius: pxToRem(4),
        '@media (max-width: 1024px)': {
            width: '45%',
        },
    },
    searchIcon: {
        color: '#A78966',
        fontSize: pxToRem(24),
    },
    searchFieldItem: {
        height: '100%',
        width: '100%',
        paddingLeft: theme.spacing(1),
    },
    searchFieldText: {
        fontSize: pxToRem(13),
        marginLeft: theme.spacing(0.5),
        marginTop: theme.spacing(0.2),
        padding: theme.spacing(0.875, 0, 0.875),
    },
    elementTypeButton: {
        backgroundColor: theme.palette.common.white,
        borderRadius: pxToRem(100),
        '&:hover': {
            backgroundColor: theme.palette.common.white,
        },
        minWidth: pxToRem(127),
        '@media (max-width: 1200px)': {
            minWidth: 'unset',
        },
    },
    activeElementTypeButton: {
        border: '1px solid #33B55B',
    },
    elementTypeItem: {
        paddingLeft: theme.spacing(2.5),
        '@media (max-width: 1024px)': {
            paddingLeft: theme.spacing(1.25),
        },
    },
    rightSideContents: {
        maxWidth: '40%',
        top: pxToRem(1),
        position: 'relative',
        right: pxToRem(8),
        '@media (max-width: 1024px)': {
            maxWidth: '50%',
        },
    },
    categoryStyling: {
        paddingLeft: theme.spacing(0.6),
        color: '#5E5F61',
        fontSize: pxToRem(13),
        float: 'left',
        display: 'contents',
        marginTop: theme.spacing(1),
    },
    checkIcon: {
        color: '#33B55B',
    },
    checkIconDisabled: {
        color: 'rgba(218, 218, 218, 0.5)',
    },
    advancedFilterText: {
        color: '#A78966',
        fontSize: pxToRem(14),
        textDecoration: 'underline',
    },
    elementTypeItems: {
        marginRight: theme.spacing(3),
    },
    elementTypeLabel: {
        padding: theme.spacing(0, 1),
        [theme.breakpoints.down('xs')]: {
            width: pxToRem(30),
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        '@media (max-width: 1024px)': {
            padding: theme.spacing(0, 0.75),
        },
    },
    clearIcon: {
        color: 'rgba(0, 0, 0, 0.54)',
        fontSize: pxToRem(24),
    },
    adornmentButton: {
        padding: theme.spacing(0, 1.5),
        '&:hover': {
            background: 'transparent',
        },
    },
    searchButton: {
        borderLeft: '1px solid #C9C9C9',
        borderRadius: 0,
        height: pxToRem(33),
    },
}))

enum FormattedElementType {
    ASSEMBLY = 'Assembly',
    PRODUCTFAMILY = 'Family',
    PRODUCTTYPE = 'Product Type',
}

export interface SearchBarProps {
    selectedCatergory?: CategoryPanel | null
    handleSearchElementNodes: (input: GetProjectElementNodesInput) => void
    resetSearchFilters: () => void
    /**
     * Called when the user submits a whitespace/dash-only query. The backend
     * rejects empty searches, so the parent must render the "No sections
     * match your search" empty state without firing the query.
     */
    onWhitespaceSearch?: () => void
    searchKeyword?: string
    searchFilters?: NodeSearchFilter[]
    selectedElementFormat?: string
}

const SearchBar: React.FC<SearchBarProps> = ({
    selectedCatergory,
    handleSearchElementNodes,
    resetSearchFilters,
    onWhitespaceSearch,
    searchKeyword,
    searchFilters,
    selectedElementFormat,
}) => {
    const classes = useShowHideBarStyles()
    const { projectId } = useParams<{ projectId?: string }>()
    const prevCategory = usePrevious(selectedCatergory)

    const [includedElements, setIncludedElements] = React.useState<
        { element: FormattedElementType; isIncluded: boolean }[]
    >(
        searchFilters
            ? Object.values(FormattedElementType).map((elementType) => {
                  const projectElementType =
                      elementType === 'Family'
                          ? ('PRODUCTFAMILY' as ProjectElementType)
                          : (elementType
                                .replace(/\s/g, '')
                                .toUpperCase() as ProjectElementType)
                  if (
                      searchFilters.find(
                          (filter) =>
                              filter.projectElementType === projectElementType
                      )
                  ) {
                      return { element: elementType, isIncluded: true }
                  }
                  return { element: elementType, isIncluded: false }
              })
            : Object.values(FormattedElementType).map((elType) => {
                  return { element: elType, isIncluded: true }
              })
    )

    const [popperAnchorEl] = React.useState<HTMLElement | null>(null)
    const searchBarRef = React.useRef<HTMLElement | null>(null)
    const [searchText, setSearchText] = React.useState<string>(
        searchKeyword ? unescapeSearchText(searchKeyword) : ''
    )

    // Sync searchText with searchKeyword prop when it changes. Unescape first
    // since searchKeyword comes from searchFilters which contains escaped text.
    React.useEffect(() => {
        if (searchKeyword !== undefined) {
            const unescapedKeyword = unescapeSearchText(searchKeyword)
            // Skip sync when searchKeyword is just the normalized form of the
            // currently typed text — that's the round-trip from our own
            // goSearch dispatch and would clobber the user's typed dashes.
            const currentNormalized = normalizeSearchText(searchText)
            if (
                unescapedKeyword !== searchText &&
                currentNormalized !== unescapedKeyword
            ) {
                setSearchText(unescapedKeyword)
            }
        }
    }, [searchKeyword])

    const modifyIncludedElements = (elementType: FormattedElementType) => {
        let elementTypeIndex = includedElements.findIndex(
            (element) => element.element === elementType
        )

        let copyOfElementTypes = JSON.parse(JSON.stringify(includedElements))
        if (elementTypeIndex > -1) {
            let elementType = copyOfElementTypes[elementTypeIndex]
            copyOfElementTypes[elementTypeIndex] = {
                ...elementType,
                isIncluded: !elementType.isIncluded,
            }
        }

        setIncludedElements(copyOfElementTypes)
    }

    /*commenting this out for version 2
    const handleDisplayAdvancedFilters = () => {
        setPopperAnchorEl(searchBarRef?.current)
    }*/

    const setTextToSearch = useCallback(
        (searchTextValue: string) => {
            setSearchText(searchTextValue)
            if (searchTextValue === '') {
                resetSearchFilters()
            }
        },
        [setSearchText, resetSearchFilters]
    )

    const clearSearch = () => {
        setSearchText('')
        resetSearchFilters()
    }

    const goSearch = useCallback(() => {
        if (searchText === '') return

        const normalizedSearchText = normalizeSearchText(searchText)

        if (normalizedSearchText === '') {
            // Whitespace/dash-only input. Backend rejects empty search text;
            // signal the parent to render the empty-state UI directly.
            onWhitespaceSearch?.()
            return
        }

        // Escape tilde (~) character so it's treated as literal text in backend queries
        // This is done internally - user still sees ~ in the search box
        const escapedSearchText = escapeSearchText(normalizedSearchText)

        handleSearchElementNodes({
            input: {
                limit: MaxElementsPerBatch,
                categoryId:
                    selectedCatergory?.id === 'all'
                        ? undefined
                        : (selectedCatergory?.id as string),
                skip: 0,
                onlyElementsAssignedToProject: true,
                projectId,
                projectElementType: Object.keys(
                    ProjectElementType
                ) as ProjectElementType[],
                isProductFamilyGroupView: selectedElementFormat === 'DIVISIONS',
                searchFilters: includedElements
                    .filter((elementType) => elementType.isIncluded)
                    .map((elementType) => {
                        const projectElementType =
                            elementType.element === 'Family'
                                ? ('PRODUCTFAMILY' as ProjectElementType)
                                : (elementType.element
                                      .replace(/\s/g, '')
                                      .toUpperCase() as ProjectElementType)
                        return {
                            projectElementType,
                            searchText: escapedSearchText,
                        }
                    }),
            },
        })
    }, [
        includedElements,
        selectedCatergory,
        searchText,
        handleSearchElementNodes,
        onWhitespaceSearch,
        selectedElementFormat,
        projectId,
    ])

    const handleKeyDown = useCallback(
        (
            event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => {
            if (event.key === 'Enter') {
                goSearch()
            }
        },
        [goSearch]
    )

    React.useEffect(() => {
        if (
            prevCategory &&
            JSON.stringify(prevCategory) !==
                JSON.stringify(selectedCatergory) &&
            !searchFilters &&
            !searchKeyword // Don't clear if searchKeyword prop has a value
        ) {
            setSearchText('')
            setIncludedElements(
                Object.values(FormattedElementType).map((elType) => {
                    return { element: elType, isIncluded: true }
                })
            )
        }
    }, [selectedCatergory, prevCategory, searchFilters, searchKeyword])

    return (
        <AppBar
            elevation={0}
            position="relative"
            className={classes.searchBarContainer}
            ref={searchBarRef}
            data-testid="searchBar"
        >
            <ContainerMaxWidthXl>
                <GridContainer justifyContent="space-between">
                    <GridItem className={classes.searchFieldContainer}>
                        <Input
                            disableUnderline
                            fullWidth
                            ref={searchBarRef}
                            inputProps={{ 'aria-label': 'search' }}
                            classes={{
                                input: classes.searchFieldText,
                                root: classes.searchFieldItem,
                            }}
                            value={searchText}
                            onChange={(ev) => setTextToSearch(ev.target.value)}
                            onKeyDown={handleKeyDown}
                            startAdornment={
                                <InputAdornment position="start">
                                    <>
                                        <span
                                            className={classes.categoryStyling}
                                            data-testid={`category-${
                                                selectedCatergory?.name ??
                                                'All Categories'
                                            }`}
                                        >
                                            {`in:${
                                                selectedCatergory?.name ??
                                                'All Categories'
                                            }`}
                                        </span>
                                    </>
                                </InputAdornment>
                            }
                            endAdornment={
                                <InputAdornment position="end">
                                    {searchText !== '' && (
                                        <IconButton
                                            onClick={clearSearch}
                                            aria-label="clear search field"
                                            classes={{
                                                root: classes.adornmentButton,
                                            }}
                                            disableRipple
                                            data-testid="clearSearch"
                                        >
                                            <ClearIcon
                                                className={classes.clearIcon}
                                            />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        classes={{
                                            root: clsx(
                                                classes.adornmentButton,
                                                classes.searchButton
                                            ),
                                        }}
                                        onClick={goSearch}
                                        disableRipple
                                        data-testid="btnSearchField"
                                        aria-label="search button"
                                        role="button"
                                    >
                                        <SearchIcon
                                            className={classes.searchIcon}
                                        />
                                    </IconButton>
                                </InputAdornment>
                            }
                            data-testid="searchField"
                        />
                        <AdvancedSearch
                            anchorEl={popperAnchorEl}
                            projectId={projectId}
                        ></AdvancedSearch>
                    </GridItem>

                    <GridItem className={classes.rightSideContents}>
                        <GridContainer spacing={2} alignItems="center">
                            <GridItem className={classes.elementTypeItems}>
                                <GridContainer>
                                    {includedElements.map((elementType) => {
                                        if (
                                            selectedElementFormat ===
                                                'DIVISIONS' &&
                                            elementType?.element === 'Assembly'
                                        ) {
                                            return <></>
                                        } else {
                                            const familyItemName =
                                                selectedElementFormat ===
                                                    'DIVISIONS' &&
                                                elementType?.element ===
                                                    'Family'
                                                    ? 'Section'
                                                    : elementType?.element
                                            return (
                                                <GridItem
                                                    key={elementType.element}
                                                    className={
                                                        classes.elementTypeItem
                                                    }
                                                >
                                                    <Button
                                                        disableRipple
                                                        endIcon={<CheckIcon />}
                                                        className={clsx(
                                                            classes.elementTypeButton,
                                                            elementType.isIncluded &&
                                                                classes.activeElementTypeButton
                                                        )}
                                                        classes={{
                                                            endIcon: elementType.isIncluded
                                                                ? classes.checkIcon
                                                                : classes.checkIconDisabled,
                                                            label:
                                                                classes.elementTypeLabel,
                                                        }}
                                                        onClick={(ev) =>
                                                            modifyIncludedElements(
                                                                elementType.element
                                                            )
                                                        }
                                                        data-testid={`${elementType.element}-filter`}
                                                    >
                                                        {elementType.element ===
                                                        'Family'
                                                            ? familyItemName
                                                            : elementType.element}
                                                    </Button>
                                                </GridItem>
                                            )
                                        }
                                    })}
                                </GridContainer>
                            </GridItem>
                            {/* for version 2
                        <GridItem>
                            <Button
                                className={classes.advancedFilterText}
                                onClick={handleDisplayAdvancedFilters}
                                disableRipple
                                data-testid="advancedFilters"
                            >
                                Advanced Filter Search
                            </Button>
                        </GridItem> */}
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </ContainerMaxWidthXl>
        </AppBar>
    )
}

export default SearchBar
